import React from 'react';
import { MemoType } from '../../types/api';
import { MemoFormState } from '../../types/dispense';
import { Doctor, Patient } from '../../types/api';
import { getTodayISO } from '../../utils/date';

const MEMO_TYPES = [
  { type: MemoType.Referral, label: 'Referral', icon: '📋' },
  { type: MemoType.Memo, label: 'Memo', icon: '📝' },
  { type: MemoType.Prescription, label: 'Prescription', icon: '💊' },
  { type: MemoType.HFMD, label: 'HFMD', icon: '🤒' },
  { type: MemoType.Chickenpox, label: 'Chickenpox', icon: '🦠' },
  { type: MemoType.POCT, label: 'POCT', icon: '🧪' },
  { type: MemoType.Vaccination, label: 'Vaccination', icon: '💉' },
  { type: MemoType.HAJ, label: 'HAJ', icon: '🏥' }
];

interface MemoTypeModalProps {
  show: boolean;
  doctor: Doctor | null;
  patient: Patient;
  patientDetails: Patient;
  setShow: (show: boolean) => void;
  setSelectedMemoType: (type: MemoType | null) => void;
  setMemoForm: (form: MemoFormState) => void;
  setShowMemoModal: (show: boolean) => void;
}

export function MemoTypeModal({
  show, doctor, patient, patientDetails,
  setShow, setSelectedMemoType, setMemoForm, setShowMemoModal
}: MemoTypeModalProps) {
  if (!show) return null;

  const handleClose = () => {
    setShow(false);
    setSelectedMemoType(null);
  };

  const handleSelectType = (type: MemoType) => {
    setSelectedMemoType(type);
    const gender = patientDetails.gender === 'M' ? 'he' : 'she';
    const Gender = patientDetails.gender === 'M' ? 'He' : 'She';
    const patientInfo = `${patientDetails.name} ${patientDetails.nric}`;
    let defaultRecipient = '', defaultBody = '';
    if (type === MemoType.Prescription) {
      defaultRecipient = 'Pharmacist';
      defaultBody = `Dear Colleague,\n\nKindly dispense the following for the abovementioned:\n\n`;
    } else if (type === MemoType.HFMD) {
      defaultRecipient = 'School / Workplace';
      defaultBody = `Dear Sir / Madam,\n\nI have examined the abovementioned person and I find that ${gender} has recovered from Hand, Foot and Mouth Disease.\n\n${Gender} is fit to return to school / work.`;
    } else if (type === MemoType.Memo) {
      defaultRecipient = 'Whom This May Concern';
      defaultBody = `Dear Sir / Madam,\n\n\n`;
    } else if (type === MemoType.Referral) {
      defaultRecipient = '';
      defaultBody = `Dear Colleague,\n\n\n`;
    } else if (type === MemoType.Chickenpox) {
      defaultRecipient = 'School / Workplace';
      defaultBody = `Dear Sir / Madam,\n\nI have examined the abovementioned person and I find that ${gender} has recovered from Chickenpox.\n\n${Gender} is fit to return to school / work.`;
    }
    // Generate FormattedSignatureLine matching CheckLine's Employee.FormattedSignatureLine
    let defaultSignOff = '';
    if (doctor?.maskOn) {
      defaultSignOff = 'Locum Physician';
    } else {
      defaultSignOff = `${doctor?.displayName || doctor?.name || ''}   MCR ${doctor?.mcr || ''}`;
      if (doctor?.customText1) {
        defaultSignOff += `\n${doctor.customText1}`;
      }
      if (doctor?.customText2) {
        defaultSignOff += `\n${doctor.customText2}`;
      }
      defaultSignOff += `\n${patient?.store || ''}`;
    }
    setMemoForm({
      recipient: defaultRecipient,
      subHeader: patientInfo,
      body: defaultBody,
      signOff: defaultSignOff,
      memoDate: getTodayISO()
    });
    // Close type selection modal and open memo form modal
    setShow(false);
    setShowMemoModal(true);
  };

  return (
    <div
      className="modal-overlay"
      onClick={handleClose}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(4px)',
        zIndex: 2500, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '20px', animation: 'fadeIn 0.2s ease-out'
      }}
    >
      <div
        className="modal modal-large"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: '600px', background: '#ffffff', borderRadius: '20px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', display: 'flex', flexDirection: 'column',
          overflow: 'hidden', animation: 'slideUpModal 0.3s ease-out'
        }}
      >
        {/* Header */}
        <div style={{
          padding: '24px 28px', borderBottom: '2px solid #e5e7eb',
          background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 50%, #bae6fd 100%)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '22px', fontWeight: 700, color: '#0c4a6e', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '28px' }}>📄</span>
              Select Memo Type
            </div>
          </div>
          <button onClick={handleClose} style={{
            background: 'rgba(255, 255, 255, 0.8)', border: '2px solid rgba(0, 0, 0, 0.1)', borderRadius: '12px',
            width: '44px', height: '44px', fontSize: '24px', cursor: 'pointer', display: 'flex',
            alignItems: 'center', justifyContent: 'center', color: '#64748b', fontWeight: 300
          }}>&times;</button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '28px', background: '#fafbfc' }}>
          <div style={{
            background: '#ffffff', borderRadius: '16px', padding: '24px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)', border: '2px solid #e5e7eb'
          }}>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '12px', display: 'block' }}>
                Choose a memo type <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px' }}>
                {MEMO_TYPES.map(({ type, label, icon }) => (
                  <button
                    key={type}
                    onClick={() => handleSelectType(type)}
                    style={{
                      padding: '16px', border: '2px solid #e5e7eb', borderRadius: '12px',
                      background: '#ffffff', cursor: 'pointer', transition: 'all 0.2s ease',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px',
                      fontSize: '14px', fontWeight: 600, color: '#374151'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#0284c7';
                      e.currentTarget.style.background = '#f0f9ff';
                      e.currentTarget.style.color = '#0284c7';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.background = '#ffffff';
                      e.currentTarget.style.color = '#374151';
                    }}
                  >
                    <span style={{ fontSize: '24px' }}>{icon}</span>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
